/**
 * Main onboarding page with tabbed navigation
 * Allows non-linear progression through onboarding sections
 */

import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Building, CreditCard, Printer, Globe, Laptop, MapPin, Truck } from 'lucide-react';

import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingSection, OnboardingStatus } from '@/types/onboarding';

// Import tab components
import OnboardingHeader from '@/components/onboarding/OnboardingHeader';
import OnboardingLayout from '@/components/onboarding/OnboardingLayout';
import BusinessDetailsTab from '@/components/onboarding/tabs/BusinessDetailsTab';
import StripeConnectTab from '@/components/onboarding/tabs/StripeConnectTab';
import PrinterSetup from '@/components/onboarding/integrations/PrinterSetup';
import DomainSetup from '@/components/onboarding/integrations/DomainSetup';
import WebsiteIntegration from '@/components/onboarding/integrations/WebsiteIntegration';
import GoogleBusinessSetup from '@/components/onboarding/integrations/GoogleBusinessSetup';
import AggregatorSetup from '@/components/onboarding/integrations/AggregatorSetup';

// Icon mapping for sections
const SECTION_ICONS = {
  [OnboardingSection.BUSINESS_DETAILS]: Building,
  [OnboardingSection.STRIPE_CONNECT]: CreditCard,
  [OnboardingSection.PRINTER]: Printer,
  [OnboardingSection.DOMAIN]: Globe,
  [OnboardingSection.WEBSITE]: Laptop,
  [OnboardingSection.GOOGLE_BUSINESS]: MapPin,
  [OnboardingSection.AGGREGATORS]: Truck,
};

// Status color mapping
const STATUS_COLORS = {
  [OnboardingStatus.NOT_STARTED]: 'secondary',
  [OnboardingStatus.AWAITING_CONFIRMATION]: 'default',
  [OnboardingStatus.NEEDS_ATTENTION]: 'destructive',
  [OnboardingStatus.COMPLETE]: 'success',
} as const;

// Status text mapping
const STATUS_TEXT = {
  [OnboardingStatus.NOT_STARTED]: 'Not Started',
  [OnboardingStatus.AWAITING_CONFIRMATION]: 'Awaiting Review',
  [OnboardingStatus.NEEDS_ATTENTION]: 'Needs Attention',
  [OnboardingStatus.COMPLETE]: 'Complete',
};

const OnboardingPage: React.FC = () => {
  const {
    onboardingData,
    progress,
    activeSection,
    setActiveSection,
    isLoading,
    error,
  } = useOnboarding();

  const [currentTab, setCurrentTab] = useState<string>(OnboardingSection.BUSINESS_DETAILS);

  // Loading state
  if (isLoading) {
    return (
      <div className="w-full p-6">
        <div className="space-y-6">
          {/* Header skeleton */}
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
            <Skeleton className="h-32 w-full" />
          </div>
          
          {/* Tabs skeleton */}
          <div className="space-y-4">
            <div className="flex space-x-2">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-32" />
              ))}
            </div>
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load onboarding data. Please refresh the page or contact support if the issue persists.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // No data state
  if (!onboardingData || !progress) {
    return (
      <div className="w-full p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No onboarding data found. Please contact support.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Render tab trigger with status badge
  const renderTabTrigger = (section: OnboardingSection, title: string) => {
    const Icon = SECTION_ICONS[section];
    const sectionProgress = progress.sections.find(s => s.section === section);
    const status = sectionProgress?.status || OnboardingStatus.NOT_STARTED;
    
    return (
      <TabsTrigger
        key={section}
        value={section}
        className="flex flex-col items-center gap-1 px-1 sm:px-2 py-1.5 text-xs sm:text-sm font-medium h-auto data-[state=active]:text-white group"
      >
        <div className="flex items-center gap-1.5">
          <Icon className="h-4 w-4 flex-shrink-0 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
          <span className="hidden md:inline text-center leading-tight group-data-[state=active]:text-white">{title}</span>
        </div>
        <Badge 
          variant={STATUS_COLORS[status]}
          className="text-[10px] px-2 py-0.5 mt-0.5"
        >
          {STATUS_TEXT[status]}
        </Badge>
      </TabsTrigger>
    );
  };

  return (
    <OnboardingLayout
      activeSection={currentTab}
      onSectionChange={setCurrentTab}
    >
      <div className="space-y-6">
        {/* Header with progress visualization */}
        <OnboardingHeader progress={progress} />

        {/* Main onboarding content */}
        <Card>
          <CardHeader>
            <CardTitle>Complete Your Onboarding</CardTitle>
            <CardDescription>
              Review and complete each section below. You can work on them in any order, 
              but all required sections must be completed before your account is fully activated.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-1 h-auto p-1">
                {renderTabTrigger(OnboardingSection.BUSINESS_DETAILS, 'Business Details')}
                {renderTabTrigger(OnboardingSection.STRIPE_CONNECT, 'Payments')}
                {renderTabTrigger(OnboardingSection.DOMAIN, 'Domain')}
                {renderTabTrigger(OnboardingSection.WEBSITE, 'Website')}
                {renderTabTrigger(OnboardingSection.GOOGLE_BUSINESS, 'Google')}
                {renderTabTrigger(OnboardingSection.AGGREGATORS, 'Aggregators')}
                {renderTabTrigger(OnboardingSection.PRINTER, 'Printer')}
              </TabsList>

              {/* Tab Contents */}
              <div className="mt-6">
                <TabsContent value={OnboardingSection.BUSINESS_DETAILS} className="space-y-4">
                  <BusinessDetailsTab />
                </TabsContent>

                <TabsContent value={OnboardingSection.STRIPE_CONNECT} className="space-y-4">
                  <StripeConnectTab />
                </TabsContent>

                <TabsContent value={OnboardingSection.PRINTER} className="space-y-4">
                  <PrinterSetup />
                </TabsContent>

                <TabsContent value={OnboardingSection.DOMAIN} className="space-y-4">
                  <DomainSetup onNavigateToSection={setCurrentTab} />
                </TabsContent>

                <TabsContent value={OnboardingSection.WEBSITE} className="space-y-4">
                  <WebsiteIntegration onNavigateToSection={setCurrentTab} />
                </TabsContent>

                <TabsContent value={OnboardingSection.GOOGLE_BUSINESS} className="space-y-4">
                  <GoogleBusinessSetup />
                </TabsContent>

                <TabsContent value={OnboardingSection.AGGREGATORS} className="space-y-4">
                  <AggregatorSetup />
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* Help section */}
        <Card>
          <CardHeader>
            <CardTitle>Need Help?</CardTitle>
            <CardDescription>
              Our support team is here to help you get set up successfully.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-medium">Contact Support</h4>
                <p className="text-sm text-muted-foreground">
                  Email: support@pumpd.co.nz<br />
                  Phone: +64 22 311 3693
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">Remote Assistance</h4>
                <p className="text-sm text-muted-foreground">
                  For technical integrations, our team can provide remote assistance.
                  Simply choose "Remote Access" in the relevant sections.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingPage;